import type { ReactNode } from "react"

import { humanizeKey } from "@/admin/monitoring/run-presentation"
import { terminalControlVariants } from "@/components/terminal/control-variants"
import { cn } from "@/lib/utils"

export const reviewKinds = [
  "company",
  "acquisition",
  "intake",
  "individual",
] as const
export type ReviewKind = (typeof reviewKinds)[number]

export const labelByReviewKind: Record<ReviewKind, string> = {
  company: "Company Scope",
  acquisition: "Reported deal",
  intake: "Intake",
  individual: "Individual",
}

export const reviewKind = (review: {
  isIndividual: boolean
  isIntakeRaised: boolean
  isReportedDeal: boolean
}): ReviewKind => {
  if (review.isIndividual) return "individual"
  if (review.isIntakeRaised) return "intake"
  if (review.isReportedDeal) return "acquisition"
  return "company"
}

// Identity Adjudication's verdicts in a person's words, toned by what they
// suggest: a tie is good news, a contradiction points at discarding. Rank is
// the order a person works through them: settled-looking verdicts first.
const dataByVerdict: Record<
  string,
  { label: string; tone: string; fill: string; rank: number }
> = {
  same_enterprise: {
    label: "Same business",
    tone: "text-signal-good",
    fill: "bg-signal-good",
    rank: 0,
  },
  same_individual: {
    label: "Same person",
    tone: "text-signal-good",
    fill: "bg-signal-good",
    rank: 0,
  },
  related_not_same: {
    label: "Related, not same",
    tone: "text-signal-warn",
    fill: "bg-signal-warn",
    rank: 1,
  },
  different_enterprise: {
    label: "Different business",
    tone: "text-signal-bad",
    fill: "bg-signal-bad",
    rank: 2,
  },
  different_individual: {
    label: "Different people",
    tone: "text-signal-bad",
    fill: "bg-signal-bad",
    rank: 2,
  },
  cannot_tell: {
    label: "Can't tell",
    tone: "text-text-secondary",
    fill: "bg-text-secondary",
    rank: 3,
  },
}

const notAdjudicated = {
  label: "Not adjudicated",
  tone: "text-text-disabled",
  fill: "bg-border-strong",
  rank: 5,
}

export const verdictData = (verdict: string | null) =>
  verdict
    ? (dataByVerdict[verdict] ?? {
        label: humanizeKey(verdict),
        tone: "text-text-secondary",
        fill: "bg-text-secondary",
        rank: 4,
      })
    : notAdjudicated

export const VerdictLabel = ({
  verdict,
  className,
}: {
  verdict: string | null
  className?: string
}) => {
  const data = verdictData(verdict)
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 whitespace-nowrap",
        data.tone,
        className
      )}
    >
      <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />
      {data.label}
    </span>
  )
}

export const formatReviewAge = (hours: number) =>
  hours < 48 ? `${Math.round(hours)}h` : `${Math.round(hours / 24)}d`

export const primaryControl = terminalControlVariants({
  size: "l",
  intent: "primary",
})

export const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className="border-border-subtle text-text-secondary hidden rounded-sm border px-1 font-mono text-xs leading-none sm:inline-block">
    {children}
  </kbd>
)

// Page shortcuts stay out of the way while a person types.
export const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
